// frontend-react/src/utils/helpers.js
/**
 * Utilitaires partagés pour le frontend FEDIS
 * ============================================
 * Ce fichier centralise les fonctions utilitaires utilisées
 * dans plusieurs composants pour éviter la duplication de code.
 */

/* =========================
 * Classes CSS
 * ========================= */

/**
 * Combine des classes CSS en filtrant les valeurs falsy
 * @param {...string} xs - Classes à combiner
 * @returns {string} Classes combinées
 */
export function cls(...xs) {
  return xs.filter(Boolean).join(' ');
}

/* =========================
 * Configuration dynamique
 * ========================= */

let _appLocale = 'fr-FR';
let _appCurrency = 'FCFA';

/**
 * Met a jour la config de formatage (appele au login apres fetch /api/settings)
 */
export function setAppConfig({ locale, currency } = {}) {
  if (locale) _appLocale = locale;
  if (currency) _appCurrency = currency;
}

/* =========================
 * Formatage Monétaire
 * ========================= */

/**
 * Formate un nombre selon la locale configuree (sans devise)
 */
export function formatNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString(_appLocale) : '0';
}

/**
 * Formate un nombre avec la devise configuree
 */
export function formatMoney(v) {
  return formatNumber(v) + ' ' + _appCurrency;
}

/**
 * Alias pour formatNumber (compatibilité)
 */
export const money = formatNumber;

/**
 * Alias pour formatMoney (compatibilité)
 */
export const fmtMoney = formatMoney;

/**
 * Formate avec la devise configuree
 */
export function fmtMoneyXOF(v) {
  return formatNumber(v) + ' ' + _appCurrency;
}

/* =========================
 * Formatage Dates
 * ========================= */

/**
 * Formate une date selon la locale configuree
 */
export function formatDateFR(d) {
  if (!d) return '—';
  const dt = d instanceof Date ? d : new Date(d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString(_appLocale);
}

/**
 * Convertit une date en format ISO (YYYY-MM-DD)
 */
export function toYYYYMMDD(d) {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toISOString().slice(0, 10);
}

/**
 * Alias pour toYYYYMMDD
 */
export const isoDate = toYYYYMMDD;

/**
 * Retourne le premier jour du mois
 */
export function startOfMonth(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/**
 * Retourne le dernier jour du mois
 */
export function endOfMonth(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

/**
 * Soustrait des jours à une date
 */
export function subDays(d, days) {
  const x = new Date(d);
  x.setDate(x.getDate() - days);
  return x;
}

/* =========================
 * Conversion
 * ========================= */

/**
 * Convertit une valeur en nombre avec valeur par défaut
 */
export function toNum(v, def = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}

/**
 * Arrondi monétaire à 2 décimales
 */
export function toMoney(v) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
}

/**
 * Normalise une chaîne (trim + conversion)
 */
export function safeStr(v) {
  return String(v ?? '').trim();
}

/* =========================
 * Validation
 * ========================= */

/**
 * Vérifie si une valeur est un UUID valide
 */
export function isUuid(v) {
  return (
    typeof v === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v)
  );
}

/**
 * Vérifie si une valeur est un email valide
 */
export function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || ''));
}

/**
 * Vérifie si une valeur est un numéro de téléphone valide
 */
export function isValidPhone(phone) {
  const cleaned = String(phone || '').replace(/\D/g, '');
  return cleaned.length >= 8 && cleaned.length <= 15;
}

/* =========================
 * URL & Query Params
 * ========================= */

/**
 * Construit une query string à partir d'un objet
 */
export function buildQueryString(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      qs.set(key, String(value));
    }
  });
  return qs.toString();
}

/**
 * Ajoute une query string à une URL
 */
export function urlWithParams(baseUrl, params = {}) {
  const qs = buildQueryString(params);
  return qs ? baseUrl + '?' + qs : baseUrl;
}

/* =========================
 * UI Helpers
 * ========================= */

/**
 * Retourne les classes pour un badge selon le ton
 */
export function getBadgeClasses(tone = 'gray') {
  const tones = {
    gray: 'bg-slate-100 text-slate-700 border-slate-200',
    green: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    red: 'bg-rose-50 text-rose-700 border-rose-100',
    blue: 'bg-sky-50 text-sky-700 border-sky-100',
    indigo: 'bg-slate-50 text-slate-800 border-slate-100',
    orange: 'bg-amber-50 text-amber-800 border-amber-100',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
  };
  return tones[tone] || tones.gray;
}

/**
 * Retourne le badge de statut de paiement
 */
export function getPaymentBadge(order) {
  const mode = safeStr(order?.payment?.mode || order?.payment_mode || '').toLowerCase();
  const status = safeStr(order?.payment?.status || order?.payment_status || '').toUpperCase();

  const total = toNum(order?.payment?.total_ttc ?? order?.total_ttc);
  const paid = toNum(order?.payment?.paid_total ?? order?.paid_total);

  let label = 'CASH';
  if (mode === 'credit' || mode === 'installments') label = 'CREDIT';

  let state = status || '';
  if (!state && total > 0) {
    if (paid >= total) state = 'PAID';
    else if (paid > 0) state = 'PARTIAL';
    else if (label === 'CREDIT') state = 'PENDING';
    else state = 'CASH';
  }

  if (state === 'PAID') return { text: 'PAYÉ', cls: 'bg-green-100 text-green-800 border-green-200' };
  if (state === 'PARTIAL') return { text: 'PARTIEL', cls: 'bg-amber-100 text-amber-800 border-amber-200' };
  if (state === 'PENDING') return { text: 'EN ATTENTE', cls: 'bg-red-100 text-red-800 border-red-200' };
  return { text: 'CASH', cls: 'bg-gray-100 text-gray-800 border-gray-200' };
}

/* =========================
 * Debounce & Throttle
 * ========================= */

/**
 * Crée une fonction debounced
 */
export function debounce(fn, delay = 300) {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Crée une fonction throttled
 */
export function throttle(fn, limit = 300) {
  let inThrottle;
  return (...args) => {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}
